import { Card } from "@fpsoftware/mvp";
import { useSuspenseQuery } from "@tanstack/react-query";
import cn from "classnames";
import { TbCheck, TbCreditCard, TbSettings } from "react-icons/tb";
import { formatInstantDate, type ProfileResponse } from "shared";
import { Api } from "@/api.config";
import { useProfile } from "@/auth/useProfile";
import { Button } from "@/components/Button";
import { Container } from "@/components/grid/Container";
import { Grid } from "@/components/grid/Grid";
import { QueryKeys } from "@/LinkGen";
import { AppPage } from "@/layout/AppLayout";
import { useSubscriptionActions } from "@/subscription/subscription";
import styles from "./PageSubscription.module.css";
import agreementSvg from "./undraw_agreement_ftet.svg";

function SubscriptionDetails(props: {
  subscription: NonNullable<ProfileResponse["subscription"]>;
}) {
  const { subscription } = props;

  const status = subscription.cancelAt ? "Canceled" : "Active";

  return (
    <div className={styles.subDetails}>
      <div
        className={cn(styles.subStatus, {
          [styles.subStatusCanceled]: status === "Canceled",
        })}
      >
        {status}
      </div>
      <dl className={styles.subGrid}>
        <dt className={styles.subGridLabel}>Start Date</dt>
        <dd className={styles.subGridValue}>
          {formatInstantDate(subscription.startAt)}
        </dd>
        <dt className={styles.subGridLabel}>Valid Until</dt>
        <dd className={styles.subGridValue}>
          {formatInstantDate(subscription.endAt)}
        </dd>
      </dl>
    </div>
  );
}

export const PageSubscription = () => {
  const { profile } = useProfile();
  const { checkout, portal } = useSubscriptionActions();

  const { data: pricing } = useSuspenseQuery({
    queryKey: QueryKeys.StripePricing(),
    queryFn: () => Api.StripePricing().then((res) => res.data),
  });

  const formatPrice = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(amount / 100);
  };

  return (
    <AppPage title="Subscription">
      <Container>
        <Grid className="mb-3">
          <Grid.Col xs={12}>
            <Card>
              <Card.Header>Why Borrow Genius?</Card.Header>
              <Card.Body>
                <div className={styles.splitLayout}>
                  <ul className={styles.benefits}>
                    <li className={styles.benefitItem}>
                      <TbCheck size={18} className={styles.benefitIcon} />
                      Be the advisor clients trust — not just one of many quotes
                    </li>
                    <li className={styles.benefitItem}>
                      <TbCheck size={18} className={styles.benefitIcon} />
                      Show clients exactly why their payment changes, down to
                      the dollar
                    </li>
                    <li className={styles.benefitItem}>
                      <TbCheck size={18} className={styles.benefitIcon} />
                      Reveal the true long-term cost or savings of every
                      refinance option
                    </li>
                    <li className={styles.benefitItem}>
                      <TbCheck size={18} className={styles.benefitIcon} />
                      Catch what others miss — escrow resets, prepaid interest,
                      buydown erosion
                    </li>
                    <li className={styles.benefitItem}>
                      <TbCheck size={18} className={styles.benefitIcon} />
                      Back every recommendation with precise math, not guesswork
                    </li>
                    <li className={styles.benefitItem}>
                      <TbCheck size={18} className={styles.benefitIcon} />
                      Move conversations from "Is this a good rate?" to "Does
                      this fit my plan?"
                    </li>
                    <li className={styles.benefitItem}>
                      <TbCheck size={18} className={styles.benefitIcon} />
                      Build lasting client relationships through transparency
                      and objectivity
                    </li>
                    <li className={styles.benefitItem}>
                      <TbCheck size={18} className={styles.benefitIcon} />
                      Stand out as a trusted financial guide in a crowded market
                    </li>
                  </ul>
                  <div className={styles.divider} />
                  <div className={styles.illustration}>
                    <img
                      src={agreementSvg}
                      alt="Agreement illustration"
                      className={styles.illustrationImg}
                    />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Grid.Col>
        </Grid>
        <Card className="mb-3">
          <Card.Header>Subscription</Card.Header>
          <Card.Body>
            <div className={styles.splitLayout}>
              <div>
                <h4 className={styles.includedTitle}>What's Included</h4>
                <ul className={styles.benefits}>
                  <li className={styles.benefitItem}>
                    <TbCheck size={18} className={styles.benefitIcon} />
                    Full access to Borrow Genius
                  </li>
                  <li className={styles.benefitItem}>
                    <TbCheck size={18} className={styles.benefitIcon} />
                    Unlimited refinance scenarios
                  </li>
                  <li className={styles.benefitItem}>
                    <TbCheck size={18} className={styles.benefitIcon} />
                    Client-ready PDF reports
                  </li>
                  <li className={styles.benefitItem}>
                    <TbCheck size={18} className={styles.benefitIcon} />
                    White-label company branding
                  </li>
                  <li className={styles.benefitItem}>
                    <TbCheck size={18} className={styles.benefitIcon} />
                    Auto-calculated compensation and discount points
                  </li>
                  <li className={styles.benefitItem}>
                    <TbCheck size={18} className={styles.benefitIcon} />
                    Automatic property tax lookups
                  </li>
                  <li className={styles.benefitItem}>
                    <TbCheck size={18} className={styles.benefitIcon} />
                    Automatic escrow projections
                  </li>
                </ul>
              </div>
              <div className={styles.divider} />
              {profile.subscription ? (
                <div className={styles.pricing}>
                  <SubscriptionDetails subscription={profile.subscription} />
                </div>
              ) : (
                <div className={styles.pricing}>
                  <p className={styles.price}>
                    {formatPrice(
                      pricing.initial.amount,
                      pricing.initial.currency,
                    )}
                    <span className={styles.priceInterval}>/ month</span>
                  </p>
                  <p className={styles.trial}>
                    for the first {pricing.initialPhaseMonths} months, then{" "}
                    {formatPrice(
                      pricing.standard.amount,
                      pricing.standard.currency,
                    )}
                    /month
                  </p>
                </div>
              )}
            </div>
          </Card.Body>
          <Card.Footer>
            <div className={styles.footer}>
              {profile.subscription ? (
                <>
                  <span />
                  <Button
                    icon={TbSettings}
                    onClick={() => portal.mutate()}
                    loading={portal.isPending}
                  >
                    Manage Subscription
                  </Button>
                </>
              ) : (
                <>
                  <p className={styles.finePrint}>Cancel anytime.</p>
                  <Button
                    icon={TbCreditCard}
                    onClick={() => checkout.mutate()}
                    loading={checkout.isPending}
                  >
                    Subscribe
                  </Button>
                </>
              )}
            </div>
          </Card.Footer>
        </Card>
      </Container>
    </AppPage>
  );
};
